import {
  WrapperDiv,
  ContentWrapperDiv,
  LeftDoubleSkipStyles,
  LeftSkipStyles,
  RightDoubleSkipStyles,
  RightSkipStyles,
  PaginationUl,
} from './Pagination.styles';

import {useState, useEffect, Component} from 'react';

interface IPaginatorList {
  totalNumberOfItems: number;
  onChangePage: Function;
  initialPage?: number;
  maxPages?: number;
  status?: Array<string>;
  initCall: boolean;
  getPageSize: Function;
  type?: string;
  ThumbnailComponent?: Component;
  initialCallback?: Function;
  shouldDataReinitialize?: boolean;
  thumbnailList?: Array<string>;
  pushPaginationGA?: Function;
  linkable?: boolean;
}

interface IPager {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  startPage: number;
  pages: Array<number>;
}

const Pagination = ({
  onChangePage,
  initialPage = 1,
  getPageSize,
  totalNumberOfItems = 0,
  maxPages = 6,
  status = ['default'],
  initCall,
  type = 'numbers',
  ThumbnailComponent,
  initialCallback = () => {},
  shouldDataReinitialize = true,
  thumbnailList = [],
  pushPaginationGA = () => {},
  linkable = false,
}: IPaginatorList) => {
  const calculateNumberOfPages = (totalItemCount, perPageFunction) => {
    let itemsCovered = 0;
    let numPages = 0;
    while (itemsCovered <= totalItemCount) {
      numPages += 1;
      itemsCovered += perPageFunction(numPages);
    }
    return numPages;
  };

  const getPageLimits = (pageNumber, pageVolumeFunction) => {
    if (pageNumber == 1) {
      return {offset: 0, limit: pageVolumeFunction(1)};
    } else {
      var prevPageLimits = getPageLimits(pageNumber - 1, pageVolumeFunction);
      return {
        offset: prevPageLimits['offset'] + pageVolumeFunction(pageNumber - 1),
        limit: pageVolumeFunction(pageNumber),
      };
    }
  };

  const getPager = (totalItems: number, currentPage = 1, pageCount, maxPages): IPager => {
    let totalPages = pageCount;
    let startPage: number, endPage: number;
    if (totalPages <= maxPages) {
      // total pages less than max so show all pages
      startPage = 1;
      endPage = totalPages;
    } else {
      // total pages more than max so calculate start and end pages
      let maxPagesBeforeCurrentPage = Math.floor(maxPages / 2);
      let maxPagesAfterCurrentPage = Math.ceil(maxPages / 2) - 1;
      if (currentPage <= maxPagesBeforeCurrentPage) {
        // current page near the start
        startPage = 1;
        endPage = maxPages;
      } else if (currentPage + maxPagesAfterCurrentPage >= totalPages) {
        // current page near the end
        startPage = totalPages - maxPages + 1;
        endPage = totalPages;
      } else {
        // current page somewhere in the middle
        startPage = currentPage - maxPagesBeforeCurrentPage;
        endPage = currentPage + maxPagesAfterCurrentPage;
      }
    }

    let pages = Array.from(Array(endPage + 1 - startPage).keys()).map(i => startPage + i);

    // return object with all pager properties
    return {
      totalItems: totalItems,
      currentPage: currentPage,
      pageSize: pageCount,
      totalPages: totalPages,
      startPage: startPage,
      pages: pages,
    };
  };

  let count = calculateNumberOfPages(totalNumberOfItems, getPageSize);
  const pagerObj = getPager(totalNumberOfItems, initialPage, count, maxPages);

  //Setting pager configuration
  const [pager, setPager] = useState<IPager>(pagerObj as IPager);
  const [initialPageCount, setInitialPageCount] = useState(initialPage);
  const [totalCount, setTotalCount] = useState(totalNumberOfItems);
  const [initData, setInitData] = useState(initCall);

  let numPages = calculateNumberOfPages(totalNumberOfItems, getPageSize);
  const [totalPageCount, setTotalPageCount] = useState(numPages);

  useEffect(() => {
    setPage({page: initialPageCount});
    setInitialPageCount(1);
  }, [status]);

  useEffect(() => {
    // setInitialPageCount(1);
    initialCallback({onPrevNext: setPage});
  }, [pager.currentPage]);

  useEffect(() => {
    setInitialPageCount(initialPage);
  }, [initialPage]);

  useEffect(() => {
    if (shouldDataReinitialize) {
      let count = calculateNumberOfPages(totalNumberOfItems, getPageSize);
      setTotalPageCount(count);
      setTotalCount(totalNumberOfItems);
      const pagerObj = getPager(totalNumberOfItems, initialPage, count, maxPages);
      setPager(pagerObj);
    }
  }, [totalNumberOfItems, status]);

  const setPage = ({e, page}: {e?: MouseEvent; page: number}) => {
    if (e) {
      e.preventDefault();
      //for GA
      pushPaginationGA && pushPaginationGA(page);
    }

    if (page < 1 || page > pager.totalPages) {
      return;
    }
    const pagerObj = getPager(totalCount, page, totalPageCount, maxPages);

    // update config state
    setPager(pagerObj);

    let obj = getPageLimits(page, getPageSize);

    // call change page function in parent component
    if (initData) {
      if (type === 'numbers') {
        onChangePage({offset: obj.offset, limit: obj.limit});
      } else {
        onChangePage({
          offset: obj.offset,
          limit: obj.limit,
          pages: pagerObj.pages,
          currentPage: pagerObj.currentPage,
        });
      }
    } else {
      setInitData(true);
    }
  };

  if (!pager.pages || pager.pages.length <= 1) {
    // don't display pager if there is only 1 page
    return null;
  }

  return (
    <>
      {type === 'numbers' ? (
        <WrapperDiv>
          <ContentWrapperDiv>
            <LeftDoubleSkipStyles
              className={pager.currentPage === 1 ? 'disabled' : ''}
              onClick={(e: MouseEvent) => {
                setPage({e, page: 1});
              }}
            >
              {'<<'}
            </LeftDoubleSkipStyles>
            <LeftSkipStyles
              className={pager.currentPage === 1 ? 'disabled' : ''}
              onClick={(e: MouseEvent) => {
                setPage({e, page: pager.currentPage - 1});
              }}
            >
              {'<'}
            </LeftSkipStyles>
            {linkable ? (
              <PaginationUl>
                {pager.pages.map((page, index) => (
                  <li
                    key={index}
                    className={pager.currentPage === page ? 'active' : ''}
                    onClick={(e: MouseEvent) => setPage({e, page})}
                  >
                    <a>{page}</a>
                  </li>
                ))}
              </PaginationUl>
            ) : (
              <PaginationUl>
                {pager.pages.map((page, index) => (
                  <li
                    key={index}
                    className={pager.currentPage === page ? 'active' : ''}
                    onClick={(e: MouseEvent) => setPage({e, page})}
                  >
                    {page}
                  </li>
                ))}
              </PaginationUl>
            )}

            <RightSkipStyles
              className={pager.currentPage === pager.totalPages ? 'disabled' : ''}
              onClick={(e: MouseEvent) => setPage({e, page: pager.currentPage + 1})}
            >
              {'>'}
            </RightSkipStyles>
            <RightDoubleSkipStyles
              className={pager.currentPage === pager.totalPages ? 'disabled' : ''}
              onClick={(e: MouseEvent) => setPage({e, page: totalPageCount})}
            >
              {'>>'}
            </RightDoubleSkipStyles>
          </ContentWrapperDiv>
        </WrapperDiv>
      ) : (
        <>
          {thumbnailList &&
            thumbnailList.length > 0 &&
            thumbnailList.map((item, i) => (
              <li
                key={i}
                onClick={(e: MouseEvent) => setPage({e, page: pager.pages[i]})}
                className={pager.pages[i] === pager.currentPage ? 'active' : ''}
              >
                <ThumbnailComponent sourceImg={item} />
              </li>
            ))}
        </>
      )}
    </>
  );
};

export default Pagination;
